var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getLocalParticipant } from '../base/participants/functions';
import { extractFqnFromPath } from '../dynamic-branding/functions.any';
import { DETECT_FACE, FACE_BOX_EVENT_TYPE, SEND_IMAGE_INTERVAL_MS } from './constants';
import logger from './logger';
let canvas;
let context;
if (typeof OffscreenCanvas === 'undefined') {
    canvas = document.createElement('canvas');
    context = canvas.getContext('2d');
}
/**
 * Sends the face expression with its duration to all the other participants.
 *
 * @param {any} conference - The current conference.
 * @param  {string} faceExpression - Face expression to be sent.
 * @param {number} duration - The duration of the face expression in seconds.
 * @returns {void}
 */
export function sendFaceExpressionToParticipants(conference, faceExpression, duration) {
    try {
        conference.sendEndpointMessage('', {
            type: 'face_landmark',
            faceExpression,
            duration
        });
    }
    catch (err) {
        logger.warn('Could not broadcast the face expression to the other participants', err);
    }
}
/**
 * Sends the face box to all the other participants.
 *
 * @param {any} conference - The current conference.
 * @param  {FaceBox} faceBox - Face box to be sent.
 * @returns {void}
 */
export function sendFaceBoxToParticipants(conference, faceBox) {
    try {
        conference.sendEndpointMessage('', {
            type: FACE_BOX_EVENT_TYPE,
            faceBox
        });
    }
    catch (err) {
        logger.warn('Could not broadcast the face box to the other participants', err);
    }
}
/**
 * Sends the face expression with its duration to xmpp server.
 *
 * @param {any} conference - The current conference.
 * @param  {string} faceExpression - Face expression to be sent.
 * @param {number} duration - The duration of the face expression in seconds.
 * @returns {void}
 */
export function sendFaceExpressionToServer(conference, faceExpression, duration) {
    try {
        conference.sendFaceLandmarks({
            faceExpression,
            duration
        });
    }
    catch (err) {
        logger.warn('Could not send the face expression to xmpp server', err);
    }
}
/**
 * Sends face expression to backend.
 *
 * @param  {Object} state - Redux state.
 * @returns {boolean} - True if sent, false otherwise.
 */
export function sendFaceExpressionsWebhook(state) {
    return __awaiter(this, void 0, void 0, function* () {
        const { webhookProxyUrl: url } = state['features/base/config'];
        const { conference } = state['features/base/conference'];
        const { jwt } = state['features/base/jwt'];
        const { connection } = state['features/base/connection'];
        const jid = connection === null || connection === void 0 ? void 0 : connection.getJid();
        const localParticipant = getLocalParticipant(state);
        const { faceExpressionsBuffer } = state['features/face-landmarks'];
        if (faceExpressionsBuffer.length === 0) {
            return false;
        }
        const headers = Object.assign(Object.assign({}, jwt ? { 'Authorization': `Bearer ${jwt}` } : {}), { 'Content-Type': 'application/json' });
        const reqBody = {
            meetingFqn: extractFqnFromPath(),
            sessionId: conference === null || conference === void 0 ? void 0 : conference.sessionId,
            submitted: Date.now(),
            emotions: faceExpressionsBuffer,
            participantId: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.jwtId,
            participantName: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.name,
            participantJid: jid
        };
        if (url) {
            try {
                const res = yield fetch(`${url}/emotions`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(reqBody)
                });
                if (res.ok) {
                    return true;
                }
                logger.error('Status error:', res.status);
            }
            catch (err) {
                logger.error('Could not send request', err);
            }
        }
        return false;
    });
}
/**
 * Sends the image data a canvas from the track in the image capture to the face recognition worker.
 *
 * @param {Worker} worker - Face recognition worker.
 * @param {Object} imageCapture - Image capture that contains the current track.
 * @param {number} threshold - Movement threshold as percentage for sharing face coordinates.
 * @returns {Promise<boolean>} - True if sent, false otherwise.
 */
export function sendDataToWorker(worker, imageCapture, threshold = 10) {
    return __awaiter(this, void 0, void 0, function* () {
        if (imageCapture === null || imageCapture === undefined) {
            return false;
        }
        let imageBitmap;
        let image;
        try {
            imageBitmap = yield imageCapture.grabFrame();
        }
        catch (err) {
            logger.warn(err);
            return false;
        }
        if (typeof OffscreenCanvas === 'undefined') {
            canvas.width = imageBitmap.width;
            canvas.height = imageBitmap.height;
            context === null || context === void 0 ? void 0 : context.drawImage(imageBitmap, 0, 0);
            image = context === null || context === void 0 ? void 0 : context.getImageData(0, 0, imageBitmap.width, imageBitmap.height);
        }
        else {
            image = imageBitmap;
        }
        worker.postMessage({
            type: DETECT_FACE,
            image,
            threshold
        });
        imageBitmap.close();
        return true;
    });
}
/**
 * Gets face box for a participant id.
 *
 * @param {string} id - The participant id.
 * @param {IReduxState} state - The redux state.
 * @returns {Object}
 */
function getFaceBoxForId(id, state) {
    return state['features/face-landmarks'].faceBoxes[id];
}
/**
 * Gets the video object position for a participant id.
 *
 * @param {IReduxState} state - The redux state.
 * @param {string} id - The participant id.
 * @returns {string} - CSS object-position in the shape of '{horizontalPercentage}% {verticalPercentage}%'.
 */
export function getVideoObjectPosition(state, id) {
    const faceBox = id && getFaceBoxForId(id, state);
    if (faceBox) {
        const { right, width } = faceBox;
        if (right && width) {
            return `${right - (width / 2)}% 50%`;
        }
    }
    return '50% 50%';
}
/**
 * Gets the video object position for a participant id.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {number} - Number of milliseconds for doing face detection.
 */
export function getDetectionInterval(state) {
    const { faceLandmarks } = state['features/base/config'];
    return Math.max((faceLandmarks === null || faceLandmarks === void 0 ? void 0 : faceLandmarks.captureInterval) || SEND_IMAGE_INTERVAL_MS);
}
/**
 * Returns the duration in seconds of a face expression.
 *
 * @param {IReduxState} state - The redux state.
 * @param {number} faceExpressionCount - The number of consecutive face expressions.
 * @returns {number} - Duration of face expression in seconds.
 */
export function getFaceExpressionDuration(state, faceExpressionCount) {
    return faceExpressionCount * (getDetectionInterval(state) / 1000);
}
